import { IconButton } from '@repo/foundations/components/Buttons';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { SentimentType } from '../../../components/AIAssistant/AIFeedbackModal/constants';
import { AISteps } from '../../../components/AIAssistant/Messages/AISteps';
import type { QuestionStore } from '../store';
import { QuestionReplyBody } from './QuestionReplyBody';
import { QuestionReplyInput } from './QuestionReplyInput';

export const AiLegacyReply = observer(
	({
		replyId,
		store,
		onClickPositiveFeedback,
		onClickNegativeFeedback,
	}: {
		replyId: string;
		store: QuestionStore;
		onClickPositiveFeedback?: () => void;
		onClickNegativeFeedback?: () => void;
	}) => {
		const reply = store.replies.get(replyId)!;

		const additionalToolbarButtons = useMemo(() => {
			const isPositiveFeedbackSubmitted =
				reply.reply.embedded_prompt?.user_feedback_raw?.sentiment ===
				SentimentType.Positive;
			const isNegativeFeedbackSubmitted =
				reply.reply.embedded_prompt?.user_feedback_raw?.sentiment ===
				SentimentType.Negative;

			return (
				<>
					{!!reply.reply.ai_steps?.length && (
						<IconButton
							iconName="listSearch"
							variant="tertiary"
							onClick={reply.toggleShowSteps}
							tooltip="Show AI steps"
							className="show-on-hover"
							data-active={reply.showSteps ? 'true' : undefined}
						/>
					)}
					{onClickPositiveFeedback &&
						(isPositiveFeedbackSubmitted ? (
							<IconButton
								iconName="thumbUpFilled"
								variant="tertiary"
								onClick={onClickPositiveFeedback}
								tooltip="Positive feedback for this prompt already submitted"
								className="show-on-hover"
							/>
						) : (
							<IconButton
								iconName="thumbUp"
								variant="tertiary"
								onClick={onClickPositiveFeedback}
								tooltip="Good response"
								className="show-on-hover"
							/>
						))}
					{onClickNegativeFeedback &&
						(isNegativeFeedbackSubmitted ? (
							<IconButton
								iconName="thumbDownFilled"
								variant="tertiary"
								onClick={onClickNegativeFeedback}
								tooltip="Negative feedback for this prompt already submitted"
								className="show-on-hover"
							/>
						) : (
							<IconButton
								iconName="thumbDown"
								variant="tertiary"
								onClick={onClickNegativeFeedback}
								tooltip="Bad response"
								className="show-on-hover"
							/>
						))}
				</>
			);
		}, [
			onClickPositiveFeedback,
			onClickNegativeFeedback,
			reply.reply.embedded_prompt?.user_feedback_raw?.sentiment,
			reply.reply.ai_steps?.length,
			reply.toggleShowSteps,
			reply.showSteps,
		]);

		return (
			<QuestionReplyBody
				replyId={replyId}
				store={store}
				additionalToolbarButtons={additionalToolbarButtons}
			>
				<QuestionReplyInput
					key={reply.reply.id}
					dataTestId={`reply-editor-${reply.reply.id}`}
					readOnly
					initialValue={reply.reply.definition}
				/>
				{reply.showSteps && reply.reply.ai_steps && (
					<AISteps steps={reply.reply.ai_steps} />
				)}
			</QuestionReplyBody>
		);
	}
);
